import { Link } from "@/lib/router-compat";
import { ArrowRight } from "lucide-react";
import type { SiteLang } from "@/hooks/useLanguage";

type CTACopy = { title: string; paragraphs: string[]; cta: string; href: string };

const ALIGNMENT_COPY: Record<SiteLang, CTACopy> = {
  en: {
    title: "Align Your CDx Submission Strategy From the Start",
    paragraphs: [
      "Combined drug-diagnostic development requires teams to navigate two regulatory frameworks without allowing gaps to emerge between them.",
      "QbD Group and PrecisionDX support CDx programmes across pharmaceutical and diagnostic development, helping teams align CTR and IVDR requirements, structure combined-study governance, and prepare submissions that work within both regulatory frameworks.",
      "Planning or preparing a combined CDx study in Europe? Get in touch with our experts to discuss how to align your clinical trial and IVDR performance study strategy from the start.",
    ],
    cta: "Get in touch with our experts",
    href: "/en/contact",
  },
  es: {
    title: "Alinea tu estrategia de presentación de CDx desde el principio",
    paragraphs: [
      "El desarrollo combinado de medicamento y producto sanitario obliga a los equipos a moverse entre dos marcos regulatorios sin dejar que aparezcan huecos entre ellos.",
      "QbD Group y PrecisionDX apoyan programas de CDx a lo largo del desarrollo farmacéutico y diagnóstico, ayudando a los equipos a alinear los requisitos del CTR y del IVDR, estructurar la gobernanza del estudio combinado y preparar solicitudes que funcionen en ambos marcos regulatorios.",
      "¿Estás planificando o preparando un estudio combinado de CDx en Europa? Ponte en contacto con nuestros expertos para hablar de cómo alinear desde el inicio tu ensayo clínico y tu estudio de funcionamiento bajo el IVDR.",
    ],
    cta: "Ponte en contacto con nuestros expertos",
    href: "/es/contact",
  },
  "zh-cn": {
    title: "从一开始就对齐您的 CDx 提交策略",
    paragraphs: [
      "药物-器械联合开发要求团队在两个法规框架之间穿行，同时不让二者之间出现空白。",
      "QbD Group 和 PrecisionDX 在制药与诊断开发领域支持 CDx 项目，帮助团队对齐 CTR 与 IVDR 要求、构建联合研究治理并准备符合两套法规框架的提交材料。",
      "正在欧洲规划或准备联合 CDx 研究？请联系我们的专家，讨论如何从一开始就对齐您的临床试验与 IVDR 性能研究策略。",
    ],
    cta: "联系我们的专家",
    href: "/zh-cn/contact",
  },
};

const COPY: Record<SiteLang, CTACopy> = {
  en: {
    title: "Get Your CDx Governance Right From the Start",
    paragraphs: [
      "Combined drug-diagnostic studies bring together regulatory frameworks, organisations and responsibilities that do not always align neatly.",
      "Defining IVDR sponsorship early helps establish clear accountability and provides the foundation for submission planning, monitoring, safety reporting and study governance throughout the programme.",
      "QbD Group and PrecisionDX can support CDx programmes across the clinical and diagnostic development pathway, helping teams translate regulatory responsibilities into a practical governance model.",
      "Working on a combined CDx study in Europe? Get in touch with our experts to discuss how to structure your IVDR responsibilities from the start.",
    ],
    cta: "Get in touch with our experts",
    href: "/en/contact",
  },
  es: {
    title: "Define bien la gobernanza de tu CDx desde el principio",
    paragraphs: [
      "Los estudios combinados de medicamento y producto sanitario reúnen marcos regulatorios, organizaciones y responsabilidades que no siempre encajan con facilidad.",
      "Definir el rol de promotor IVDR desde el inicio ayuda a establecer una responsabilidad clara y aporta la base para la planificación de la solicitud, la monitorización, la notificación de seguridad y la gobernanza del estudio durante todo el programa.",
      "QbD Group y PrecisionDX pueden apoyar programas de CDx a lo largo de todo el recorrido de desarrollo clínico y diagnóstico, ayudando a los equipos a traducir las responsabilidades regulatorias en un modelo de gobernanza práctico.",
      "¿Trabajas en un estudio combinado de CDx en Europa? Ponte en contacto con nuestros expertos para hablar de cómo estructurar tus responsabilidades bajo el IVDR desde el principio.",
    ],
    cta: "Ponte en contacto con nuestros expertos",
    href: "/es/contact",
  },
  "zh-cn": {
    title: "从一开始就建立正确的 CDx 治理体系",
    paragraphs: [
      "联合药物-器械研究汇集了并不总是 neatly 对齐的法规框架、组织和责任。",
      "尽早明确 IVDR 申办者有助于建立清晰的责任制，并为整个项目的提交规划、监查、安全性报告和研究治理奠定基础。",
      "QbD Group 和 PrecisionDX 可以在临床和诊断开发路径上支持 CDx 项目，帮助团队将法规责任转化为实用的治理模式。",
      "正在欧洲开展联合 CDx 研究？请联系我们的专家，讨论如何从一开始就构建您的 IVDR 责任。",
    ],
    cta: "联系我们的专家",
    href: "/zh-cn/contact",
  },
};

const MONITORING_COPY: Record<SiteLang, CTACopy> = {
  en: {
    title: "Make Monitoring Rights Part of Combined-Study Governance From the Start",
    paragraphs: [
      "In a combined study, monitoring responsibilities cannot safely be inferred from who already visits the site or which organisation appears to own a particular activity.",
      "Test sites and sample collection sites generate evidence that may ultimately support the diagnostic regulatory file. The rights, responsibilities, escalation pathways and governance surrounding that evidence need to be established accordingly.",
      "QbD Group and PrecisionDX support combined CDx programmes across pharmaceutical and diagnostic development, helping teams translate CTR and IVDR requirements into practical study governance, monitoring and regulatory processes.",
      "Setting up a combined study in Europe? Get in touch with our experts to make sure monitoring responsibilities are clear before your first site is activated.",
    ],
    cta: "Get in touch with our experts",
    href: "/en/contact",
  },
  es: {
    title: "Integra los derechos de monitorización en la gobernanza del CDx desde el principio",
    paragraphs: [
      "En un estudio combinado de CDx, las responsabilidades de monitorización no pueden deducirse con seguridad de quién visita ya el centro o de qué organización parece «dueña» de una actividad concreta.",
      "Los centros de análisis y los centros de recogida de muestras generan evidencia que puede acabar sustentando el expediente regulatorio del diagnóstico. Los derechos, las responsabilidades, las vías de escalado y la gobernanza sobre esa evidencia deben establecerse en consecuencia.",
      "QbD Group y PrecisionDX apoyan programas combinados de CDx a lo largo del desarrollo farmacéutico y diagnóstico, ayudando a los equipos a traducir los requisitos del CTR y del IVDR en procesos prácticos de gobernanza, monitorización y regulatorios.",
      "¿Estás poniendo en marcha un estudio combinado de CDx en Europa? Ponte en contacto con nuestros expertos para asegurarte de que las responsabilidades de monitorización están claras antes de activar tu primer centro.",
    ],
    cta: "Ponte en contacto con nuestros expertos",
    href: "/es/contact",
  },
  "zh-cn": {
    title: "从一开始就将监查权纳入 CDx 治理",
    paragraphs: [
      "在联合 CDx 研究中，监查职责不能仅凭谁已经访视该中心或哪家机构看似“拥有”某项活动来推断。",
      "检测中心和样本采集中心产生的证据最终可能支持诊断产品的法规档案。围绕这些证据的权利、职责、升级路径和治理必须相应地明确建立。",
      "QbD Group 和 PrecisionDX 在制药与诊断开发领域支持联合 CDx 项目，帮助团队将 CTR 与 IVDR 要求转化为切实可行的研究治理、监查与法规流程。",
      "正在欧洲筹备联合 CDx 研究？请联系我们的专家，在第一个中心启动前明确监查职责。",
    ],
    cta: "联系我们的专家",
    href: "/zh-cn/contact",
  },
};

interface CDxGovernanceCTAProps {
  lang: SiteLang;
  variant?: "governance" | "alignment" | "monitoring";
}

export default function CDxGovernanceCTA({ lang, variant = "governance" }: CDxGovernanceCTAProps) {
  const source =
    variant === "alignment" ? ALIGNMENT_COPY : variant === "monitoring" ? MONITORING_COPY : COPY;
  const c = source[lang] || source.en;
  return (
    <div className="my-12 rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/[0.04] to-accent/[0.04] p-6 sm:p-8">
      <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-primary/70 mb-2">
        QbD Group
      </p>
      <h3 className="text-lg sm:text-xl font-bold text-foreground leading-snug mb-4">
        {c.title}
      </h3>
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed mb-5">
        {c.paragraphs.map((p, i) =>
          i === 2 ? (
            <p key={i} className="font-semibold text-foreground">
              {p}
            </p>
          ) : (
            <p key={i}>{p}</p>
          )
        )}
      </div>
      <Link
        to={c.href}
        className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80 transition-colors"
      >
        {c.cta}
        <ArrowRight size={14} />
      </Link>
    </div>
  );
}
